import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { FileText, Plus, Search, ClipboardList, EyeOff } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTimezone } from '../context/TimezoneContext';
import { ThemedText, Button, Input, Pagination, ActionMenu, SkeletonCards, ErrorState } from '../components';
import { Layout } from '../components/Layout';
import { formSchemaService } from '../api';
import { formatDateOnly } from '../utils/dateUtils';
import { usePagination } from '../hooks/usePagination';
import { useReadOnly } from '../hooks/useReadOnly';
import { StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import { renderFormIcon } from '../utils/iconUtils';

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;
  flex-wrap: wrap;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
`;

const FormsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.sm};
  right: ${props => props.theme.spacing.sm};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: ${props => props.theme.colors.muted};
`;

const FormIcon = styled.div`
  font-size: 32px;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FormTitle = styled(ThemedText)`
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const FormMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.sm};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const StatusBadge = styled.span`
  padding: 2px ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  background-color: ${props =>
    props.status === 'active'
      ? `${props.theme.colors.tint}20`
      : props.status === 'draft'
        ? `${props.theme.colors.warning || '#ffc107'}20`
        : `${props.theme.colors.muted}20`
  };
  color: ${props =>
    props.status === 'active'
      ? props.theme.colors.tint
      : props.status === 'draft'
        ? props.theme.colors.warning || '#ffc107'
        : props.theme.colors.muted
  };
`;

const SubmissionCount = styled.div`
  text-align: right;
`;

const HiddenFormBadge = styled.span`
  padding: 2px ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  background-color: ${props => `${props.theme.colors.tint}15`};
  color: ${props => props.theme.colors.tint};
  margin-left: ${props => props.theme.spacing.xs};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    cursor: pointer;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${props => props.theme.spacing.lg};
  opacity: 0.5;
`;

export const FormsPage = () => {
  const theme = useTheme();
  const { effectiveTimezone } = useTimezone();
  const readOnly = useReadOnly();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [includeHiddenForms, setIncludeHiddenForms] = useState(false);

  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);

  // Fetch forms from API
  useEffect(() => {
    const fetchForms = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const response = await formSchemaService.getFormSchemas({
          includeInactive: false,
          includeHiddenForms: includeHiddenForms,
          ...paginationParams
        });

        const { items, totalCount: total } = extractPagedData(response);
        setForms(items);
        setTotalCount(total);
      } catch (error) {
        console.error('Failed to fetch forms:', error);
        setFetchError(error?.response?.data?.message || 'Failed to load forms. Please check your connection and try again.');
        toast.error('Failed to load forms');
        setForms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, [currentPage, includeHiddenForms]);

  // Filter forms based on search term (client-side)
  const filteredForms = useMemo(() => {
    if (!searchTerm.trim()) return forms;

    return forms.filter(form =>
      form.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [forms, searchTerm]);

  const currentItems = Array.isArray(filteredForms) ? filteredForms : [];
  const displayTotalCount = searchTerm ? filteredForms.length : totalCount;

  // Reset to first page when search changes
  useEffect(() => {
    resetPage();
  }, [searchTerm]);

  const handleFormClick = (form) => {
    navigate(`/forms/${form.id}/submissions`);
  };

  const handleDeleteForm = async (formId, formName) => {
    if (window.confirm(`Are you sure you want to delete "${formName}"? This action cannot be undone.`)) {
      try {
        await formSchemaService.deleteFormSchema(formId);
        toast.success('Form deleted successfully');

        // Refresh forms list
        setForms(forms.filter(f => f.id !== formId));
        setTotalCount(prev => prev - 1);
      } catch (error) {
        console.error('Failed to delete form:', error);
        toast.error('Failed to delete form');
      }
    }
  };

  const handleCreateNew = () => {
    navigate('/form-builder');
  };

  const handlePageChange = (page) => {
    goToPage(page);
  };

  const formatDate = (dateString) => {
    return formatDateOnly(dateString, effectiveTimezone);
  };

  return (
    <Layout currentPath="/forms">
      <Helmet>
        <title>Forms - aQRate Portal</title>
        <meta name="description" content="Manage and view all your forms and their submissions" />
      </Helmet>

      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <HeaderLeft>
            <ThemedText type="title" style={{ marginBottom: theme.spacing.sm, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
              <FileText size={24} /> Forms
            </ThemedText>
            <ThemedText style={{ color: theme.colors.muted }}>
              Manage your data collection forms and view submission statistics
            </ThemedText>
          </HeaderLeft>

          <HeaderRight theme={theme}>
            {!readOnly && (
              <Button
                variant="primary"
                onClick={handleCreateNew}
              >
                <Plus size={16} /> New Form
              </Button>
            )}
          </HeaderRight>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search forms by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>

          <ToggleSwitch theme={theme}>
            <ToggleInput
              type="checkbox"
              checked={includeHiddenForms}
              onChange={(e) => setIncludeHiddenForms(e.target.checked)}
            />
            <ToggleSlider theme={theme} $checked={includeHiddenForms} />
            <ThemedText type="defaultSemiBold">Show hidden forms</ThemedText>
          </ToggleSwitch>
        </SearchContainer>

        {loading ? (
          <SkeletonCards count={6} theme={theme} />
        ) : fetchError ? (
          <ErrorState
            theme={theme}
            title="Failed to load forms"
            message={fetchError}
            onRetry={() => window.location.reload()}
          />
        ) : currentItems.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon><FileText size={48} /></EmptyIcon>
            <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.md }}>
              {searchTerm ? 'No forms found' : 'No forms created yet'}
            </ThemedText>
            <ThemedText style={{ color: theme.colors.muted, marginBottom: theme.spacing.lg }}>
              {searchTerm
                ? 'Try adjusting your search terms or create a new form'
                : 'Get started by creating your first data collection form'
              }
            </ThemedText>
            {!searchTerm && !readOnly && (
              <Button variant="primary" onClick={handleCreateNew}>
                <Plus size={16} /> Create Your First Form
              </Button>
            )}
          </EmptyState>
        ) : (
          <>
            <FormsGrid theme={theme}>
              {currentItems.map(form => (
                <FormCard
                  key={form.id}
                  theme={theme}
                  onClick={() => handleFormClick(form)}
                >
                  <CardActions theme={theme}>
                    <ActionMenu actions={[
                      {
                        label: 'View Submissions',
                        onClick: () => navigate(`/forms/${form.id}/submissions`)
                      },
                      {
                        label: 'Report Templates',
                        onClick: () => navigate(`/forms/${form.id}/pdf-templates`)
                      },
                      ...(readOnly ? [] : [{
                        label: 'Delete',
                        onClick: () => handleDeleteForm(form.id, form.name),
                        variant: 'danger'
                      }])
                    ]} />
                  </CardActions>

                  <FormIcon>{renderFormIcon(form.schemaDefinition?.icon || form.icon, 32)}</FormIcon>

                  <FormTitle type="defaultSemiBold">
                    {form.name}
                    {form.isHidden && (
                      <HiddenFormBadge theme={theme}><EyeOff size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Hidden</HiddenFormBadge>
                    )}
                  </FormTitle>

                  <ThemedText
                    type="small"
                    style={{
                      color: theme.colors.muted,
                      marginBottom: theme.spacing.md,
                      lineHeight: 1.4
                    }}
                  >
                    {form.description}
                  </ThemedText>

                  <FormMeta theme={theme}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                      <StatusBadge theme={theme} status={form.isActive ? 'active' : 'inactive'}>
                        {form.isActive ? 'active' : 'inactive'}
                      </StatusBadge>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        Updated {formatDate(form.updatedAt || form.createdAt)}
                      </ThemedText>
                    </div>

                    <SubmissionCount>
                      <ThemedText type="defaultSemiBold" style={{ color: theme.colors.tint }}>
                        {form.submissionCount || 0}
                      </ThemedText>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        submissions
                      </ThemedText>
                    </SubmissionCount>
                  </FormMeta>
                </FormCard>
              ))}
            </FormsGrid>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(displayTotalCount / pageSize) || 1}
              totalCount={displayTotalCount}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={handlePageChange}
              itemLabel="forms"
            />
          </>
        )}
      </PageContainer>
    </Layout>
  );
};